import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, resource, signal, untracked } from '@angular/core';

import type { SavedListingRelationship, SavedSearch } from '../../types/saved-items';
import type { UrlParams } from '../../types/urlParams';

import { Environment } from '../../../environments/environment';
import { StateService } from '../state/state';
import { AvailableAreasParams } from './available-areas';
import { AvailableModelsParams } from './available-models';
import { AvailableSeriesParams } from './available-series';
import { HasFilters } from './has-filters';
import { SeriesLoader } from './loaders';
import { AreasLoader } from './loaders/areas';
import { ModelsLoader } from './loaders/models';
import { Params } from './params';
import { RelatedModels } from './related-models';
import { ConvertSaleTypes, SaleTypes } from './sale-types';
import { SetSearch } from './search';
import { SortUrlToFilter } from './sort';

@Injectable({
  providedIn: 'root',
})
export class FilteringService {
  protected readonly httpClient = inject(HttpClient);
  protected readonly stateService = inject(StateService);

  public readonly areas = signal<string[]>([]);
  public readonly availableAreas = resource({
    loader: ({ params }) => AreasLoader(this.httpClient, params()),
    params: () => AvailableAreasParams,
  });
  public readonly availableModels = resource({
    loader: ({ params }) => ModelsLoader(this.httpClient, params()),
    params: () => AvailableModelsParams,
  });
  public readonly availableSeries = resource({
    loader: ({ params }) => SeriesLoader(this.httpClient, params()),
    params: () => AvailableSeriesParams,
  });
  public readonly baths = signal<string[]>([]);
  public readonly beds = signal<string[]>([]);
  public readonly dreamHomes = signal<boolean>(false);
  public readonly garages = signal<string[]>([]);
  public readonly geoWithin = signal<number[][]>(Environment.geoWithin);
  public readonly hasFilters = computed(() => HasFilters.call(this));
  public readonly homesites = signal<string[]>([]);
  public readonly ids = signal<string[]>([]);
  public readonly ignoreFilters = signal(false);
  public readonly includeHomesites = signal(false);
  public readonly includeNewHomes = signal(true);
  public readonly includePendingSales = signal(false);
  public readonly includePreownedHomes = signal(true);
  public readonly lat = signal<null | number>(Environment.mapCenter[1]);
  public readonly limit = signal(2000);
  public readonly lng = signal<null | number>(Environment.mapCenter[0]);
  public readonly lvl = signal(Environment.mapZoom || 2);
  public readonly minSqft = signal('');
  public readonly models = signal<string[]>([]);
  public readonly openHomes = signal(false);
  public readonly params = computed(() => Params.call(this));
  public readonly pool = signal(false);
  public readonly priceHigh = signal('');
  public readonly priceLow = signal('');
  public readonly relatedModels = computed(() => RelatedModels.call(this));
  public readonly relatedSearchModelIds = signal<string[]>([]);
  public readonly reps = signal('');
  public readonly resetCount = signal(0);
  public readonly saleTypes = computed(() => SaleTypes.call(this)); // Keep this since its used in URL as well.
  public readonly search = signal<string>('');
  public readonly searchModels = signal<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Lazy
  public readonly searchOptionProps = signal<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Lazy
  public readonly searchOptions = signal<any[]>([]);
  public readonly selectedSavedSearchId = signal<string>('');
  public readonly series = signal<string[]>([]);
  public readonly sort = signal('open');
  public readonly uliKey = signal<string>('');
  public readonly unit = signal<string[]>([]);
  public readonly userDefaultSavedSearchTitle = signal<string | undefined>(undefined);
  public readonly userInitiatedSearch = signal(false);
  public readonly userSavedListingIds = signal<SavedListingRelationship[]>([]);
  public readonly userSavedSearches = signal<SavedSearch[]>([]);

  protected searchModel = { term: '' };

  private readonly _normalFilters = computed(() => [
    this.areas(),
    this.baths(),
    this.beds(),
    this.garages(),
    this.homesites(),
    !this.includeHomesites(),
    this.includeNewHomes(),
    !this.includePendingSales(),
    this.includePreownedHomes(),
    this.minSqft(),
    this.openHomes(),
    this.pool(),
    this.priceHigh(),
    this.priceLow(),
    this.series(),
    this.unit(),
  ]);

  //if all of them are false set them back to true.
  constructor() {
    // Makes sure at least one home type is always selected. If user unselects all, it will reset to all selected after 300ms.
    effect(() => {
      const _noneSelected =
        !this.includeNewHomes() && !this.includePreownedHomes() && !this.includeHomesites();

      if (_noneSelected) {
        setTimeout(() => {
          void this.resetFilters();
        });
      }
    });

    effect(() => {
      if (!this.selectedSavedSearchId()) {
        return;
      }

      setTimeout(() => {
        // if (!this.selectedSavedSearchId()) { return; }

        // const _activeSearch = this.userSavedSearches().find(s => s.id === this.selectedSavedSearchId());
        // if (_activeSearch) {
        untracked(() => {
          const _activeSearch = this.userSavedSearches().find(
            (s) => s.id === this.selectedSavedSearchId(),
          );

          if (_activeSearch) {
            const _searchString = _activeSearch.searchString.split('?');
            const _paramsArray =
              _searchString.length > 1 ? _searchString[1].split('&') : _searchString[0].split('&');
            const _params = _paramsArray.reduce((acc: Record<string, string>, param) => {
              // eslint-disable-next-line @typescript-eslint/naming-convention -- Used for URL param key.
              const [key, value] = param.split('=');
              acc[key.toLowerCase()] = value;
              return acc;
            }, {});

            void this.setFromUrlParams(_params);
          }
        });
        // }
      }, 300);
    });
  }

  public resetFilters(): Promise<void> {
    this.areas.set([]);
    this.baths.set([]);
    this.beds.set([]);
    this.dreamHomes.set(false);
    this.garages.set([]);
    this.homesites.set([]);
    this.ids.set([]);
    this.includeHomesites.set(false);
    this.includeNewHomes.set(true);
    this.includePendingSales.set(false);
    this.includePreownedHomes.set(true);
    this.lat.set(Environment.mapCenter[1]);
    this.lng.set(Environment.mapCenter[0]);
    this.lvl.set(Environment.mapZoom);
    this.minSqft.set('');
    this.models.set([]);
    this.openHomes.set(false);
    this.pool.set(false);
    this.priceHigh.set('');
    this.priceLow.set('');
    this.reps.set('');
    this.search.set('');
    this.series.set([]);
    this.sort.set('open');
    this.uliKey.set('');
    this.unit.set([]);
    this.resetCount.update((count) => count + 1);
    this.selectedSavedSearchId.set('');
    this.ignoreFilters.set(false);

    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, 0);
    });
  }

  public async setFromUrlParams(params: UrlParams): Promise<void> {
    if (params.areas && this.areas() !== params.areas.split(',')) {
      this.areas.set(params.areas.split(','));
    }
    if (params.baths && this.baths() !== params.baths.split(',')) {
      this.baths.set(params.baths.split(','));
    }
    if (params.beds && this.beds() !== params.beds.split(',')) {
      this.beds.set(params.beds.split(','));
    }
    if (params.features && this.homesites() !== params.features.split(',')) {
      this.homesites.set(params.features.split(','));
    }
    if (params.garages && this.garages() !== params.garages.split(',')) {
      this.garages.set(params.garages.split(','));
    }
    if (params.lat && this.lat() !== Number(params.lat)) {
      this.lat.set(Number(params.lat));
    }
    if (params.lng && this.lng() !== Number(params.lng)) {
      this.lng.set(Number(params.lng));
    }
    if (params.lvl && this.lvl() !== Number(params.lvl)) {
      this.lvl.set(Number(params.lvl));
    }
    if (params.minsqft && this.minSqft() !== params.minsqft) {
      this.minSqft.set(params.minsqft);
    }
    if (params.models && this.models() !== params.models.split(',')) {
      this.models.set(params.models.split(','));
    }
    if (params.openhomes && this.openHomes() !== params.openhomes) {
      this.openHomes.set(params.openhomes);
    }
    if (params.pending?.toString() === 'true' && !this.includePendingSales()) {
      this.includePendingSales.set(true);
    }
    if (params.pool && this.pool() !== params.pool) {
      this.pool.set(params.pool);
    }
    if (params.pricehigh && this.priceHigh() !== params.pricehigh) {
      this.priceHigh.set(params.pricehigh);
    }
    if (params.pricelow && this.priceLow() !== params.pricelow) {
      this.priceLow.set(params.pricelow);
    }
    if (params.reps && this.reps() !== params.reps) {
      this.reps.set(params.reps);
    } else if (params.salesrep && this.reps() !== params.salesrep) {
      this.reps.set(params.salesrep);
    } else if (params.salesreps && this.reps() !== params.salesreps) {
      this.reps.set(params.salesreps);
    }
    if (params.saletypes) {
      ConvertSaleTypes.call(this, params.saletypes);
    }
    if (params.search && this.search() !== params.search) {
      await SetSearch.call(this, params.search);
    }
    if (params.series && this.series() !== params.series.split(',')) {
      this.series.set(params.series.split(','));
    }
    if (params.sort && this.sort() !== params.sort) {
      this.sort.set(SortUrlToFilter.call(this, params.sort));
    }
    if (params.ulikey && this.uliKey() !== params.ulikey) {
      this.uliKey.set(params.ulikey);
    }
    if (params.unit && this.unit() !== params.unit.split(',')) {
      this.unit.set(params.unit.split(','));
    }
  }

  public setRepFilter(id: string): void {
    if (!id) {
      return;
    }

    void this.resetFilters().then(() => {
      setTimeout(() => {
        this.reps.set(id);
      }, 300);
    });
  }
}
